import type { ExternalLocationHubConfig } from "@redotech/account-common/address";
import { ExternalReturnHubType } from "@redotech/account-common/address";
import { type Channel } from "@redotech/cockroachdb-codegen/congodb/channel";
import { Payer } from "@redotech/cockroachdb-codegen/congodb/payer";
import { ProvisionType } from "@redotech/cockroachdb-codegen/congodb/provision-type";
import type { JsonFormat } from "@redotech/json/format";
import {
  arrayJsonFormat,
  booleanJsonFormat,
  dateJsonFormat,
  nullableJsonFormat,
  numberJsonFormat,
  objectJsonFormat,
  stringEnumJsonFormat,
  stringJsonFormat,
  stringUnionJsonFormat,
  symbolJsonFormat,
  symbolUnionJsonFormat,
  urlJsonFormat,
} from "@redotech/json/format";
import type { Json, JsonObject } from "@redotech/json/json";
import { Currency } from "@redotech/money/currencies";
import { PriceSet } from "@redotech/redo-common-model-money-common/price-set";
import { t } from "@redotech/redo-i18n";
import { Provider } from "@redotech/redo-order-common/provider";
import { BundleData } from "@redotech/redo-return-settings-primitives-common/bundle-data";
import { ReturnProcessingLevel } from "@redotech/redo-return-settings-primitives-common/return-processing-level";
import type { ConsolidationShipToAddress } from "@redotech/redo-return-settings-primitives-common/returns-center-config";
import { ConversationPlatform } from "@redotech/redo-support-conversation-protocol-common/conversation-protocol";
import type { Bijection } from "@redotech/util/bijection";
import { bijectionRef } from "@redotech/util/bijection";
import memoize from "lodash/memoize";
import { Address } from "./address";
import { AI_VERIFICATION_PROMPTS } from "./ai-verification-prompts";
import type { DropoffProviderType } from "./dropoff-provider";
import type { ExchangeGroup } from "./exchange-group";
import { AdjustmentWithType, Settlement, type ReturnType } from "./return";
import { PickupLocation } from "./return-enums";
import {
  FieldId,
  InputPartType,
  type InputAnswer,
  type InputAnswerPart,
  type StepId,
} from "./return-flow-answers";
import {
  BundleType,
  BundleValueType,
  FlowType,
  InfoToCollect,
  InputContentType,
  InputPartSize,
  InputValidation,
  OldPickupLocation,
  ParentChildBundleType,
  ParentPropertyIdentifier,
  PickupPayer,
  ReturnRequestStatus,
} from "./return-flow-enums";
import type { Action as Action_ } from "./return-flow/action";
import { actionJsonFormat as actionJsonFormat_ } from "./return-flow/action";
import type { Condition as Condition_ } from "./return-flow/condition";
import { conditionJsonFormat as conditionJsonFormat_ } from "./return-flow/condition";
import { ExchangeAgentResult } from "./return-flow/exchange-agent/exchange-agent-events";
import type { ProcessingAction as ProcessingAction_ } from "./return-flow/processing-action";
import { processingActionJsonFormat as processingActionJsonFormat_ } from "./return-flow/processing-action";
import {
  ReturnOptionMethod,
  returnOptionMethodJsonFormat,
} from "./return-flow/return-option";
import { StoreCreditType } from "./return-flow/return-option-enums";
import { triggerTypeJsonFormat } from "./return-flow/trigger";
import type { TriggerType } from "./return-flow/trigger-enums";
import { ReturnTypeEnum } from "./return-type-enum";
import {
  isDropoffProviderType,
  RETURN_RATE_SOURCE_TYPE,
  type ReturnRateSource,
  type ReturnRateSourceSelection,
} from "./shipping/return-rate-source-selection";

export const AI_VERIFICATION_REGISTRY = {
  damaged_item_claim: {
    categories: ["item", "packaging"] as const,
    /* Customer must upload at least one image for each required category before submitting.
       If a future type needs partial fulfillment (like 1 of 3), this will need to be redesigned. */
    requiredCategories: ["item", "packaging"] as const,
    categoryDisplayNames: { item: "Item", packaging: "Packaging" },
    buildPrompt: AI_VERIFICATION_PROMPTS.damaged_item_claim as (
      categoryList: string,
      itemDetails?: string,
    ) => string,
  },
} as const;

export type AiVerificationType = keyof typeof AI_VERIFICATION_REGISTRY;

export const AI_VERIFICATION_TYPES = Object.keys(
  AI_VERIFICATION_REGISTRY,
) as AiVerificationType[];

export interface AiVerificationState {
  verificationType: AiVerificationType;
  requiredCategories: readonly string[];
  categoryDisplayNames: Readonly<Record<string, string>>;
  imageCategories: Map<string, string>;
  classifyImage: (
    url: URL,
  ) => Promise<{ category: string | null; reason: string }>;
  classificationErrors: Map<string, string>;
  classifying: boolean;
}

export const SPECIAL_RESTOCK_ID = {
  BACK_TO_FULFILLMENT: "back-to-fulfillment",
  NO_RESTOCK: "no-restock",
} as const;

export type SpecialRestockId =
  (typeof SPECIAL_RESTOCK_ID)[keyof typeof SPECIAL_RESTOCK_ID];

/** Adjustment for return value/upsell/restocking fees */
export interface Adjustment {
  flat: number;
  proportion: number;
  /**
   * Per-currency flat amounts. Keys are Currency enum values; values are the
   * flat amount expressed in that presentment currency. When a customer's
   * currency matches a key, that amount is used instead of converting `flat`
   * from the shop currency. Falls back to exchange-rate conversion when the
   * customer's currency is not listed.
   */
  currencyOverrides?: Partial<Record<Currency, number>>;
  perCurrencyAmountsEnabled?: boolean;
}

export const adjustmentIdentity: Adjustment = { flat: 0, proportion: 1 };

const currencyOverridesJsonFormat: JsonFormat<
  Partial<Record<Currency, number>>
> = {
  read(json) {
    if (json === null || typeof json !== "object" || Array.isArray(json)) {
      return {};
    }
    const result: Partial<Record<Currency, number>> = {};
    for (const key of Object.keys(json)) {
      const val = (json as Record<string, unknown>)[key];
      if (typeof val === "number") {
        result[key as Currency] = val;
      }
    }
    return result;
  },
  write(record) {
    return record as unknown as Json;
  },
};

const adjustmentJsonFormat: JsonFormat<Adjustment> = objectJsonFormat(
  { flat: numberJsonFormat, proportion: numberJsonFormat },
  {
    currencyOverrides: currencyOverridesJsonFormat,
    perCurrencyAmountsEnabled: booleanJsonFormat,
  },
);

/**
 * Return flow
 */
export interface ReturnFlow {
  /** Steps */
  steps: Step[];
  publishedAt?: Date;
  _id?: string;
  defaultResetStep?: number;
  type?: FlowType;
  nodePositions?: { x: number; y: number }[];
}

export const returnTypeToFlowType: Record<ReturnTypeEnum, FlowType> = {
  [ReturnTypeEnum.RETURN]: FlowType.RETURN,
  [ReturnTypeEnum.CLAIM]: FlowType.CLAIM,
  [ReturnTypeEnum.WARRANTY]: FlowType.WARRANTY,
  [ReturnTypeEnum.RECYCLE]: FlowType.RECYCLING,
  // TODO(withdrawal): add withdrawal flow type
  [ReturnTypeEnum.WITHDRAWAL]: FlowType.RETURN,
};

export interface ChatFlow {
  steps: ChatFlowStep[];
  publishedAt?: Date;
}

export interface RuleFlow {
  steps: RuleFlowStep[];
}

export type StepIdFn = (key: StepFn) => StepId;

export type StepFn = (id: StepIdFn) => Step;

export function stepsBuild(steps: Iterable<StepFn>): Step[] {
  return stepsBuildWithMapping(steps).steps;
}

export function stepsBuildWithMapping(steps: Iterable<StepFn>): {
  steps: Step[];
  idByStepFn: Map<StepFn, StepId>;
} {
  const set = new Set(steps);
  let id: StepId = 0;
  const idByStepFn = new Map<StepFn, StepId>();
  const getId = memoize<(key: StepFn) => StepId>((step) => {
    if (!set.has(step)) {
      throw new Error(`Step is missing: ${step}`);
    }
    const assigned = id++;
    idByStepFn.set(step, assigned);
    return assigned;
  });
  const result: Step[] = [];
  for (const step of set) {
    const id = getId(step);
    result[id] = step(getId);
  }
  return { steps: result, idByStepFn };
}

export const teamOffersExchanges = (returnFlow: ReturnFlow) => {
  for (const step of returnFlow.steps) {
    if (step.type === Step.RETURN) {
      for (const option of step.options) {
        if (option.method.type === ReturnOptionMethod.EXCHANGE) {
          return true;
        }
      }
    }
  }

  return false;
};

const nodePositionJsonFormat: JsonFormat<{ x: number; y: number }> =
  objectJsonFormat({ x: numberJsonFormat, y: numberJsonFormat }, {});

export const returnFlowJsonFormat: JsonFormat<ReturnFlow> = objectJsonFormat(
  { steps: arrayJsonFormat(bijectionRef(() => stepJsonFormat)) },
  {
    publishedAt: dateJsonFormat,
    _id: stringJsonFormat,
    defaultResetStep: numberJsonFormat,
    type: stringEnumJsonFormat(FlowType),
    nodePositions: arrayJsonFormat(bijectionRef(() => nodePositionJsonFormat)),
  },
);

export const chatFlowJsonFormat: JsonFormat<ChatFlow> = objectJsonFormat(
  {
    steps: arrayJsonFormat(
      bijectionRef(() => stepJsonFormat as JsonFormat<ChatFlowStep>),
    ),
  },
  { publishedAt: dateJsonFormat },
);

/**
 * AB test option
 */
export interface ABTestTreatment {
  /** Name */
  name: string;
  /** Percentage */
  percentage: number;
  /** Next step */
  next: StepId;
}

export const abTestTreatmentJsonFormat: JsonFormat<ABTestTreatment> =
  objectJsonFormat(
    {
      name: stringJsonFormat,
      next: bijectionRef(() => stepIdJsonFormat),
      percentage: numberJsonFormat,
    },
    {},
  );

export type InputAnswerPartWithStep = InputAnswerPart & { step: number };

const inputAnswerPartJsonFormat: JsonFormat<InputAnswerPart> = objectJsonFormat(
  {
    message: stringJsonFormat,
    value: {
      read(json: Json) {
        return typeof json === "string"
          ? json
          : (json as string[]).map((json) => new URL(json));
      },
      write(value: string | URL[]) {
        return typeof value === "string"
          ? value
          : value.map((value) => value.toString());
      },
    },
    type: bijectionRef(() => inputPartTypeJsonFormat),
  },
  { fieldId: stringEnumJsonFormat(FieldId) },
);

export const inputAnswerJsonFormat: JsonFormat<InputAnswer> = objectJsonFormat(
  { parts: arrayJsonFormat(inputAnswerPartJsonFormat) },
  {},
);

/**
 * Question option
 */
export interface MultipleChoiceOption {
  /** Name */
  name: string;
  /** Next step */
  next?: StepId;
  fieldId?: FieldId;
  code?: string;
  /** Additional information for AI exchange suggestions */
  additionalAiInfo?: string;
}

export interface ChatMultipleChoiceOption {
  name: string;
  next: StepId | null;
  nextName: string | null;
}

export const multipleChoiceOptionJsonFormat: JsonFormat<MultipleChoiceOption> =
  objectJsonFormat(
    { name: stringJsonFormat },
    {
      next: bijectionRef(() => stepIdJsonFormat),
      code: stringJsonFormat,
      fieldId: stringEnumJsonFormat(FieldId),
      additionalAiInfo: stringJsonFormat,
    },
  );

export const chatMultipleChoiceOptionJsonFormat: JsonFormat<ChatMultipleChoiceOption> =
  objectJsonFormat(
    {
      name: stringJsonFormat,
      next: nullableJsonFormat(bijectionRef(() => stepIdJsonFormat)),
      nextName: nullableJsonFormat(stringJsonFormat),
    },
    {},
  );

export const infoToCollectJsonFormat: JsonFormat<InfoToCollect> =
  stringJsonFormat as JsonFormat<InfoToCollect>;

/**
 * Conversation platform
 */
export const conversationPlatformJsonFormat: JsonFormat<ConversationPlatform> =
  stringJsonFormat as JsonFormat<ConversationPlatform>;

export const emailToSendAsJsonFormat: JsonFormat<{
  email: string;
  name: string;
  integrationKind: string;
}> = objectJsonFormat(
  {
    name: stringJsonFormat,
    email: stringJsonFormat,
    integrationKind: stringJsonFormat,
  },
  {},
);

export const payerJsonFormat: JsonFormat<Payer> =
  stringJsonFormat as JsonFormat<Payer>;

// Null in case of green return when there is no label
export type UncoveredPayer =
  | typeof Payer.Customer
  | typeof Payer.Merchant
  | null;

export const uncoveredPayerJsonFormat: JsonFormat<UncoveredPayer> =
  stringJsonFormat as JsonFormat<UncoveredPayer>;

export interface AllowedExchangeTypes {
  sameItem: boolean;
  variant: boolean;
  exchangeGroups: boolean;
  advanced: boolean;
}

export interface ReturnOption {
  /** Return method */
  method: ReturnOptionMethod;
  /** Label payer */
  labelPayer: UncoveredPayer;
  /** Whether to ship the item back (i.e. not green return) */
  ship: boolean;
  /** If true, the merchant will provide the customer with a label (and figure out charging them).
   * ship should be true because the customer is still shipping the item back.
   * Labels will NOT be covered.
   */
  manual_label?: boolean;
  /**
   * Which exchange types to allow the customer to choose.
   * Only relevant if method === ReturnOptionMethod.Exchange.
   */
  exchangeTypes: AllowedExchangeTypes;
  /** Whether to charge the customer for the new order label */
  chargeCustomerForNewOrderShipping?: boolean;
  /** Value of the return */
  adjustment: Adjustment;
  /** Whether to charge the customer for the repair fee */
  chargeRepairFee?: boolean;
  /** Used by Enso Rings to re-offer the engraving fee */
  variantExchangeAddOn?: boolean;
  /** The id of the variant exchange add-on */
  variantExchangeAddOnVariantId?: string;
  /** The text to display to the customer when offering the variant exchange add-on */
  variantExchangeAddOnTitle?: string;
  variantExchangeAddOnAcceptButton?: string;
  variantExchangeAddOnRejectButton?: string;
  /** Whether to refund points redeemed for this item */
  refundPointsRedeemedForItem?: boolean;
  /** Whether to deduct earned points from refund */
  deductEarnedPointsFromRefund?: boolean;
  /** Whether to reimburse merchant for final sale returns */
  reimburseForFinalSale?: boolean;
  /** Whether this option is still eligible for a shop now bonus even though it's method may not be 'exchange'*/
  shopNowBonusEligible?: boolean;
  /** Whether the exchange bonus is use-it-or-lose-it */
  isUseItOrLoseIt?: boolean;
  /**
   * Merchant-configured title for the discount applied to the draft order
   * created when finishing a warranty same-item exchange.
   */
  discountTitle?: string;
  /**
   * Whether to carry over Shopify line item properties (e.g. engravings,
   * monograms) from the returned line item to the new same-item or variant
   * exchange line item. Defaults to true when undefined to preserve the
   * legacy behavior. Does not affect exchange-group exchanges.
   */
  carryOverLineItemProperties?: boolean;
  /** Tags to add to the exchange draft order when this option creates one. */
  exchangeOrderTags?: string[];
}

export const exchangeTypesIdentity: AllowedExchangeTypes = {
  sameItem: true,
  variant: true,
  exchangeGroups: true,
  advanced: true,
};

export const returnOptionJsonFormat: JsonFormat<ReturnOption> =
  objectJsonFormat(
    {
      adjustment: adjustmentJsonFormat,
      labelPayer: uncoveredPayerJsonFormat,
      method: bijectionRef(() => returnOptionMethodJsonFormat),
      ship: booleanJsonFormat,

      exchangeTypes: objectJsonFormat<AllowedExchangeTypes>(
        {
          sameItem: booleanJsonFormat,
          variant: booleanJsonFormat,
          exchangeGroups: booleanJsonFormat,
          advanced: booleanJsonFormat,
        },
        {},
      ),
    },
    {
      manual_label: booleanJsonFormat,
      chargeCustomerForNewOrderShipping: booleanJsonFormat,
      chargeRepairFee: booleanJsonFormat,
      variantExchangeAddOn: booleanJsonFormat,
      variantExchangeAddOnVariantId: stringJsonFormat,
      variantExchangeAddOnTitle: stringJsonFormat,
      variantExchangeAddOnAcceptButton: stringJsonFormat,
      variantExchangeAddOnRejectButton: stringJsonFormat,
      refundPointsRedeemedForItem: booleanJsonFormat,
      deductEarnedPointsFromRefund: booleanJsonFormat,
      reimburseForFinalSale: booleanJsonFormat,
      shopNowBonusEligible: booleanJsonFormat,
      isUseItOrLoseIt: booleanJsonFormat,
      discountTitle: stringJsonFormat,
      carryOverLineItemProperties: booleanJsonFormat,
      exchangeOrderTags: arrayJsonFormat(stringJsonFormat),
    },
  );

export interface ClaimOption {
  /** Return method */
  method: ReturnOptionMethod;
  requireSendBack: boolean;
  chargeCustomerForNewOrderShipping?: boolean;
  variantExchangeAddOn?: boolean;
  variantExchangeAddOnVariantId?: string;
  variantExchangeAddOnTitle?: string;
  variantExchangeAddOnAcceptButton?: string;
  variantExchangeAddOnRejectButton?: string;
  /**
   * Whether to carry over Shopify line item properties (e.g. engravings,
   * monograms) from the returned line item to the new same-item exchange
   * line item. Defaults to true when undefined.
   */
  carryOverLineItemProperties?: boolean;
}

export const claimOptionJsonFormat: JsonFormat<ClaimOption> = objectJsonFormat(
  { method: returnOptionMethodJsonFormat, requireSendBack: booleanJsonFormat },
  {
    chargeCustomerForNewOrderShipping: booleanJsonFormat,
    variantExchangeAddOn: booleanJsonFormat,
    variantExchangeAddOnVariantId: stringJsonFormat,
    variantExchangeAddOnTitle: stringJsonFormat,
    variantExchangeAddOnAcceptButton: stringJsonFormat,
    variantExchangeAddOnRejectButton: stringJsonFormat,
    carryOverLineItemProperties: booleanJsonFormat,
  },
);

export const inputValidationJson: JsonFormat<InputValidation> =
  stringJsonFormat as JsonFormat<InputValidation>;

export type EmailStep =
  | Step.Trigger
  | Step.Wait
  | Step.SendEmail
  | Step.SendSms
  | Step.DoNothing;

// All steps should have a message field
export type ChatFlowStep =
  | Step.CloseChat
  | Step.CustomerResponse
  | Step.ChatMultipleChoice
  | Step.SendMessage
  | Step.AssignToAgent
  | Step.Reentry
  | Step.Condition
  | Step.SeeOrders
  | Step.AddConversationTag;

export type RuleFlowStep =
  | Step.Trigger
  | Step.MultipleConditions
  | Step.MultipleActions;

/** Fields that stay editable on a redo-managed (otherwise locked) step. */
export type RedoManagedEditableField = "claimMethod" | "conditionFalseBranch";

/** Locks a step in the flow builder; whitelists any still-editable fields. */
export interface RedoManagedMarker {
  redoManaged?: true;
  redoManagedEditableFields?: RedoManagedEditableField[];
}

/** Step */
export type Step = StepVariant extends infer T ? T & RedoManagedMarker : never;

export type StepVariant =
  | Step.Condition
  | Step.Input
  | Step.Information
  | Step.MultipleChoice
  | Step.Reject
  | Step.Return
  | Step.ManualReview
  | Step.Flag
  | Step.ExchangeShippingName
  | Step.DiscountCodeName
  | Step.AddExchangeOrderTags
  | Step.OverrideAdjustment
  | Step.OverrideGreenReturn
  | Step.SubmitClaim
  | Step.SubmitWarranty
  | Step.SubmitRepair
  | Step.SubmitWarrantyRegistration
  | Step.Block
  | Step.CloseChat
  | Step.CustomerResponse
  | Step.ChatMultipleChoice
  | Step.SendMessage
  | Step.AssignToAgent
  | Step.Reentry
  | Step.Trigger
  | Step.Wait
  | Step.SendEmail
  | Step.SendSms
  | Step.SeeOrders
  | Step.Action
  | Step.MultipleActions
  | Step.MultipleConditions
  | Step.FinalSaleReturn
  | Step.DoNothing
  | Step.ABTest
  | Step.EWUpsell
  | Step.AiReturnReasons
  | Step.AddReturnTag
  | Step.AddConversationTag
  | Step.DynamicBundleAdjustment
  | Step.SetReturnLocations
  | Step.CombineLabel
  | Step.SeparateLabels
  | Step.ReturnProcessingDecision
  | Step.LicensePlate
  | Step.Sort
  | Step.ReturnProcessingInformation
  | Step.Evidence
  | Step.AddUndeclaredItem
  | Step.Verification
  | Step.ProcessingAction
  | Step.VerifyItemVariant
  | Step.RedoManagedLocation;

/** Exchange */
export interface ReturnExchange {
  /** Order details */
  order: any;
  /** The variant id of the new selected variant, if variant exchange */
  newVariantId: string | null;
  /** The product id of the new selected product, if product exchange */
  newProductId: string | null;
  /** Additional data to use along with newVariantId for variant exchange using metafield */
  exchangeProductAsVariant?: ExchangeProductAsVariant;
  /** The exchange group used for the exchange, if there is one */
  exchangeGroup?: ExchangeGroup;
  /** The new item, if an exchange group was used */
  exchangeGroupItem?: ExchangeGroupItem;
  /** Account for different priced variants */
  accountForPriceDifference: boolean;
  /** Whether we should transfer the line item properties from the original item to the new item */
  transferLineItemProperties: boolean;
}

/** Credit */
export interface ReturnCredit {
  amount: number;
  type: StoreCreditType;
}

/** Refund */
export interface ReturnRefund {
  amount: number;
}

export interface ReturnLabel {
  /** Label payer */
  payer: Payer;
  /** Payment token, if paid by customer */
  paymentToken: string | null;
}

export interface DiscountAllocation {
  discountedAmount: { amount: string; currencyCode: string };
  discountedAmountSet?: PriceSet | null;
}

export interface GetOrdersRequest {
  email?: string;
  order_number?: string;
  emailOrZip?: string;
  getAllStatuses?: boolean;
  customerWidgetAuthToken?: string;
  page?: number;
}

export function pickupLocationOptions(): Map<
  PickupLocation,
  { value: PickupLocation; label: string; stepLabel: string; detail: string }
> {
  return new Map(
    [
      {
        value: PickupLocation.FRONT_DOOR,
        label: t("Front Door"),
        stepLabel: t("front door"),
        detail: t("from the front door"),
      },
      {
        value: PickupLocation.BACK_DOOR,
        label: t("Back Door"),
        stepLabel: t("back door"),
        detail: t("from the back door"),
      },
      {
        value: PickupLocation.SIDE_DOOR,
        label: t("Side Door"),
        stepLabel: t("side door"),
        detail: t("from the side door"),
      },
      {
        value: PickupLocation.OTHER,
        label: t("Other"),
        stepLabel: t("specified location"),
        detail: t("in another way"),
      },
    ].map((option) => [option.value, option]),
  );
}

export interface Pickup {
  email?: string | null;
  phone?: string | null;
  pickupDate: string;
  pickupLocation: {
    packageLocation: PickupLocation | OldPickupLocation;
    // This is required if packageLocation is "OTHER"
    specialInstructions?: string | null;
  };
  textReminder: boolean;
  pickupPayer?: PickupPayer;
  pickupReadyTime?: { hours: number; minutes: number } | undefined;
}

export interface DiscountCodeName {
  prefix?: string;
  suffix?: string;
}

export interface ReturnRequest {
  /** Information about the account requesting the return */
  requester: {
    /** Name of the account that sent the request */
    name?: string;
    /** Whether the account that sent the request is a customer (or a merchant) */
    isCustomer: boolean;
    /** The email associated with the creator of the return */
    email?: string;
  };
  /** Merchant charge to customer (non-negative); does not include shipping label */
  charge: string;
  /** The shipping fee total for all returns being created for return request */
  shippingFee: string;
  /** Shipping fee of an individual return */
  singleShippingFee: string;
  /** Whether the shipping fee is a flat rate or not */
  isFlatRate: boolean;
  /** Refund amount */
  refund: string;
  /** The total store credit, taking into account exchange fees */
  totalStoreCredit: string;
  /** The amount to collect from the customer if they fail to return for an instant exchange */
  returnCollectionHoldAmount: string | null;
  /** Return label, if there is one */
  label: ReturnLabel | null;
  /** Stripe payment token for an instant exchange */
  instantExchangePaymentToken: string | null;
  /** Items */
  items: ReturnItem[] | null;
  /** Items grouped into shipments */
  itemShipments: { merchantAddress: Address; items: ReturnItem[] }[] | null;
  /** Items from the advanced exchange cart */
  newItems: AdvancedExchangeItem[];
  /** Order ID */
  orderId: string;
  /** Status */
  status: ReturnRequestStatus;
  /** Provisioning */
  provision: ProvisionType;
  /** Address */
  shippingAddress: Address;
  /** Merchant Return Address */
  merchantAddress: Address | null; // null for rejected returns
  /** Array of stripe payment intents relating to this return. Each is stored for reference after first return is created and should not be charged. */
  orderPaymentIntents: any[];
  /** The total value of the new order including taxes and discounts */
  newOrderValue: string;
  /** The taxes associated with a new order - created via advanced exchange */
  newOrderTaxes: string;
  /** Manual price adjustment set by merchant */
  newOrderPriceAdjustment?: { type: "percentage" | "amount"; value: number };
  /** The cart discount allocations from advanced exchange */
  discountAllocations: DiscountAllocation[];
  /** Whether or not the label price was taken from customers refund or store credit values */
  labelDeductedFromCredit: boolean;
  /** The idempotency key used to create the return */
  idempotencyKey: string;
  /* The kind of return to create
      "return" | "claim" */
  returnType: ReturnType;
  /* The address to send any new items to (applies only to exchange orders)
      If undefined, this will default to the address set in shippingAddress */
  newOrderAddress?: Address;
  /** The user ID from Astra, only used for instant refunds */
  astraUserId?: string;
  /* Details about package pickup */
  pickup?: Pickup;
  /* Where the return was created */
  channel: Channel | undefined;
  /** Whether return will be dropped of in-store, this is not the same as POS return */
  inStoreReturn: boolean;
  /** Details about refund settlement */
  settlement?: Settlement;
  /* Whether the return is eligible for pickup */
  eligibleForPickup?: boolean;
  /* Pickup fee advertised to the customer */
  pickupFee?: number;
  /** Whether to offer free shipping for the new order */
  freeNewOrderShipping?: boolean;
  /** Name of the shipping line for the exchange order */
  exchangeShippingName?: string;
  /** Tags to add to the exchange draft order */
  exchangeOrderTags?: string[];
  /** The discount code name */
  discountCodeName?: DiscountCodeName;

  collectUpsellFee?: boolean;
  /** The id of ab test in the return flow */
  testId?: string;
  /** The id of the treatment in the ab test */
  treatmentId?: string;
  /** The id of the pickup variant in ab testing package pickup */
  pickupVariant?: string;
  /** The ids of the multi-order return ids to associate with the return. Will override orderId if both are present */
  orderIds?: string[];
  /** The total fee for repairs */
  repairFee?: number;
  /** Whether return is currently happening at physical POS */
  isPosReturn: boolean;
  /** Currency of the order / return */
  currency?: Currency | null;
  /** The shoppers local currency*/
  presentmentCurrency?: Currency | null;
  /** Whether they are using HappyReturns */
  happyReturnsData?: { items: unknown; retailerId?: string };
  /** The shoppers local timezone */
  customerTimeZone?: string | null;
  /** Covered by package protection plus */
  ppPlusCovered?: boolean;
  /** The ecommerce provider for the orders associated with the return */
  provider?: Provider;
}

export const returnRequestStatus: JsonFormat<ReturnRequestStatus> =
  stringJsonFormat as JsonFormat<ReturnRequestStatus>;

export interface LabelResult {
  /** URL for postage label */
  postageUrl: URL;
  /** URL for QR code */
  qrCodeUrl: URL;
  /** The shipment carrier */
  carrier: string;
}

export const labelResultJson: JsonFormat<LabelResult> = objectJsonFormat(
  {
    postageUrl: urlJsonFormat,
    qrCodeUrl: urlJsonFormat,
    carrier: stringJsonFormat,
  },
  {},
);

export interface DraftOrder {
  url: URL;
}

export const draftOrderJsonFormat: JsonFormat<DraftOrder> = objectJsonFormat(
  { url: urlJsonFormat },
  {},
);

export interface ReturnResult {
  /** Draft order, if necessary */
  draftOrder: DraftOrder | null;
  /** Shipping label */
  label: LabelResult | null;
  creditType: StoreCreditType;
  returnId: string;
}

export interface AdvancedExchangeItem {
  productId: string;
  /** Variant ID */
  variantId: string;
  /** Product title */
  title: string;
  /** Variant title */
  variantTitle: string;
  /** The price of the variant */
  price: string;
  /** The total value of the item. */
  itemValue?: string;
  /** The quantity of the variant */
  quantity: number;
  /** The variant image urls */
  images: string[];
  /** The currency code */
  currencyCode: string;
  /** Custom attributes on the line item that we want to carry through to the order */
  attributes?: { key: string; value?: string | null }[];
  /** The total tax amount of the new item */
  tax?: string;
}

export interface ExchangeGroupItem {
  /** Product ID */
  productId: string;
  /** Variant ID */
  variantId: string;
  /** Product title */
  title: string;
  /** Variant title */
  variantTitle: string;
  /** The variant image urls */
  imageSrc: string;
  /** The price of the variant */
  price?: string;
}

export interface ExchangeProductAsVariant {
  /** Product ID */
  productId: string;
  /** Variant ID */
  variantId: string;
}

export interface ReturnItem {
  /** Unique identifier for this return item */
  id: string;
  /** ID used to group return items into shipments */
  shipmentGroupID?: string;
  /** Variant exchange, if there is one */
  exchange: ReturnExchange | null;
  /** Input answers */
  inputAnswers: InputAnswer[];
  /** Requires manual review */
  isManualReview: boolean;
  /** Reason it was set to manual review */
  manualReviewReason?: string;
  /** Flagged to prevent automatic processing until unflagged */
  isFlagged: boolean;
  /** Reason it was flagged */
  flaggedReason?: string;
  /** Multiple choice answers */
  multipleChoiceAnswers: { answer: string; questionText: string }[];
  /** ?? */
  variantId: string;
  /** Reason */
  reason: string | null;
  /** Whether the item is damaged, determined by AI */
  aiDamaged?: boolean;
  /** Whether the item is a wrong product, determined by AI */
  aiWrongProduct?: boolean;
  /** Thread ID for AI return reasons, will append to other return reasons if present (which would be weird) */
  aiReturnReasonsThreadId?: string | null;
  /** List of reasons */
  reasons: string[];
  /** Reason code. Used in the public api */
  reasonCode?: string | null;
  /** Reason codes. Used in the public api */
  reasonCodes?: string[];
  /** Refund, if there is one */
  refund: boolean;
  /** Is a repair */
  repair?: boolean;
  /** Whether item should be shipped (i.e. not green return or in-store return) */
  ship: boolean;
  /** The added bonus or fee of the item being returned, if there is one */
  priceAdjustment: string | null;
  /** The price of the item before adjustment */
  price: string | null;
  /** The percent of the bundle that the item is. 100% (1) if not a bundle */
  percentOfBundle: number | null;
  /** The bundle data, if it exists */
  bundleData?: BundleData;
  /** The tax on the item */
  tax?: string;
  /** The reason for rejecting the return, if rejected */
  rejectMessage?: string;
  productName: { title: string; variantTitle: string };
  lineItemId: string;
  /** The bundle ID, if it exists */
  bundleId?: number;
  /** If the item being returned is part of a bundle that was unpackaged */
  isUnbundled?: boolean;
  /** The item is unbundled but we allow returns on exchange orders */
  allowFutureReturns?: boolean;
  /** Name of the shipping line for outgoing shipping (for exchange orders) */
  exchangeShippingName?: string;
  orderId: string;
  /** item's date of delivery */
  deliveredDate?: string;
  /** The location id of a CommentSold product. Used for restocking */
  locationId?: string;
  /** External location ID for restocking (from location flow evaluation) */
  restockExternalLocationId?: string;
  /** Merchant notes for the returned item in merchant created returns */
  merchant_submission_notes?: string | null;
  /** Merchant-uploaded images for the returned item */
  merchant_images?: string[] | null;
  /** Whether this is a TNPL contract line item */
  isTnplContractLineItem?: boolean;
  /** The pos input for the item */
  posInput?: { restockRequested?: boolean | null } | null;
  /** The exchange agent result for the item */
  exchangeAgentResult?: ExchangeAgentResult;
  /** The adjustments for the item */
  adjustments?: AdjustmentWithType[];
  /** Information about the return flow used for this item */
  flowInfo?: {
    /** The ID of the return flow used for this item */
    id?: string;
    /** The path through the return flow that the customer took */
    path: number[];
    /** The starting step index if the return was reset */
    resetPoint?: number;
  };
  /**
   * True when the shopper created this item via the "Can't find my product"
   * custom-item workflow. The merchant can edit title and price before
   * processing.
   */
  isCustomItem?: boolean;
}

export interface TextValidationSettings {
  minLength?: number;
  maxLength?: number;
  contentType?: InputContentType;
  customRegex?: string;
  customRegexErrorMessage?: string;
  useAdvancedValidation?: boolean;
}

export const textValidationSettingsJsonFormat: JsonFormat<TextValidationSettings> =
  objectJsonFormat(
    {},
    {
      minLength: numberJsonFormat,
      maxLength: numberJsonFormat,
      contentType: stringEnumJsonFormat(InputContentType),
      customRegex: stringJsonFormat,
      customRegexErrorMessage: stringJsonFormat,
      useAdvancedValidation: booleanJsonFormat,
    },
  );

export const inputPartTypeJsonFormat: JsonFormat<InputPartType> =
  stringJsonFormat as JsonFormat<InputPartType>;

export namespace Step {
  export const CONDITION = Symbol("condition");
  export const INPUT = Symbol("input");
  export const MANUAL_REVIEW = Symbol("manual_review");
  export const FLAG = Symbol("flag");
  export const INFORMATION = Symbol("information");
  export const EXCHANGE_SHIPPING_NAME = Symbol("exchange_shipping_name");
  export const DISCOUNT_CODE_NAME = Symbol("discount_code_name");
  export const ADD_EXCHANGE_ORDER_TAGS = Symbol("add_exchange_order_tags");
  export const OVERRIDE_ADJUSTMENT = Symbol("override_adjustment");
  export const OVERRIDE_GREEN_RETURN = Symbol("override_green_return");
  export const MULTIPLE_CHOICE = Symbol("multiple_choice");
  export const REJECT = Symbol("reject");
  export const RETURN = Symbol("return");
  export const SUBMIT_CLAIM = Symbol("submit_claim");
  export const SUBMIT_REPAIR = Symbol("submit_repair");
  export const SUBMIT_WARRANTY = Symbol("submit_warranty");
  export const SUBMIT_WARRANTY_REGISTRATION = Symbol(
    "submit_warranty_registration",
  );
  export const BLOCK = Symbol("block");
  export const CHAT_MULTIPLE_CHOICE = Symbol("chat_multiple_choice");
  export const CLOSE_CHAT = Symbol("close_chat");
  export const CUSTOMER_RESPONSE = Symbol("customer_response");
  export const SEND_MESSAGE = Symbol("send_message");
  export const ASSIGN_TO_AGENT = Symbol("assign_to_agent");
  export const REENTRY = Symbol("reentry");
  export const TRIGGER = Symbol("trigger");
  export const WAIT = Symbol("wait");
  export const SEND_EMAIL = Symbol("send_email");
  export const SEND_SMS = Symbol("send_sms");
  export const SEE_ORDERS = Symbol("see_orders");
  export const ADD_CONVERSATION_TAG = Symbol("add_conversation_tag");
  export const ACTION = Symbol("action");
  export const MULTIPLE_ACTIONS = Symbol("multiple_actions");
  export const MULTIPLE_CONDITIONS = Symbol("multiple_conditions");
  export const FINAL_SALE_RETURN = Symbol("final_sale_return");
  export const DO_NOTHING = Symbol("do_nothing");
  export const AB_TEST = Symbol("ab_test");
  export const EW_UPSELL = Symbol("ew_upsell");
  export const AI_RETURN_REASONS = Symbol("ai_return_reasons");
  export const ADD_RETURN_TAG = Symbol("add_return_tag");
  export const DYNAMIC_BUNDLE_ADJUSTMENT = Symbol("dynamic_bundle_adjustment");
  export const SET_RETURN_LOCATIONS = Symbol("set_return_locations");
  export const COMBINE_LABEL = Symbol("combine_label");
  export const SEPARATE_LABELS = Symbol("separate_labels");
  export const LICENSE_PLATE = Symbol("license_plate");
  export const SORT = Symbol("sort");
  export const RETURN_PROCESSING_DECISION = Symbol(
    "return_processing_decision",
  );
  export const RETURN_PROCESSING_INFORMATION = Symbol(
    "return_processing_information",
  );
  export const EVIDENCE = Symbol("evidence");
  export const ADD_UNDECLARED_ITEM = Symbol("add_undeclared_item");
  export const VERIFICATION = Symbol("verification");
  export const PROCESSING_ACTION = Symbol("processing_action");
  export const VERIFY_ITEM_VARIANT = Symbol("verify_item_variant");
  export const REDO_MANAGED_LOCATION = Symbol("redo_managed_location");
  /**
   * Conditional
   */
  export interface Condition {
    type: typeof CONDITION;
    customTitle?: string;
    /** Expression */
    condition: Condition_;
    /** Next step if true */
    nextTrue?: StepId;
    /** Next step if false */
    nextFalse?: StepId;
  }

  /**
   * Generic action used by rules
   */
  export interface Action {
    type: typeof ACTION;
    customTitle?: string;
    /** Expression */
    action: Action_;
  }

  export const conditionJsonFormat: JsonFormat<Condition> = objectJsonFormat(
    { type: symbolJsonFormat(CONDITION), condition: conditionJsonFormat_ },
    {
      customTitle: stringJsonFormat,
      nextTrue: bijectionRef(() => stepIdJsonFormat),
      nextFalse: bijectionRef(() => stepIdJsonFormat),
    },
  );

  export const actionJsonFormat: JsonFormat<Action> = objectJsonFormat(
    { type: symbolJsonFormat(ACTION), action: actionJsonFormat_ },
    { customTitle: stringJsonFormat },
  );

  export interface InputPart {
    // The main question for the input
    message: string;
    // Additional description for guiding the user through the question
    description: string;
    // An example image to show the user when asking for image upload
    exampleImage?: URL;
    // Whether it is required or not, not sure what "none" should be, maybe we could make this a boolean named required?
    validation: InputValidation;
    type: InputPartType;
    size?: string;
    fieldId?: FieldId;
    inputName?: string;
    autoComplete?: string;
    textValidationSettings?: TextValidationSettings;
    aiVerification?: AiVerificationType;
    aiVerificationStrictness?: number;
  }

  export const inputPartJson: JsonFormat<InputPart> = objectJsonFormat(
    {
      message: stringJsonFormat,
      description: stringJsonFormat,
      validation: inputValidationJson,
      type: inputPartTypeJsonFormat,
    },
    {
      exampleImage: urlJsonFormat,
      size: stringEnumJsonFormat(InputPartSize),
      fieldId: stringEnumJsonFormat(FieldId),
      inputName: stringJsonFormat,
      autoComplete: stringJsonFormat,
      textValidationSettings: textValidationSettingsJsonFormat,
      aiVerification: stringJsonFormat as JsonFormat<AiVerificationType>,
      aiVerificationStrictness: numberJsonFormat,
    },
  );

  /**
   * Customer input
   */
  export interface Input {
    type: typeof INPUT;
    customTitle?: string;
    /** Parts */
    parts: InputPart[];
    /** Next step */
    next: StepId;
  }

  export const inputJsonFormat: JsonFormat<Input> = objectJsonFormat(
    {
      type: symbolJsonFormat(INPUT),
      parts: arrayJsonFormat(inputPartJson),
      next: bijectionRef(() => stepIdJsonFormat),
    },
    { customTitle: stringJsonFormat },
  );

  export const doNothingJsonFormat: JsonFormat<Step.DoNothing> =
    objectJsonFormat({ type: symbolJsonFormat(Step.DO_NOTHING) }, {});

  /**
   * Mark for manual review
   */
  export interface ManualReview {
    type: typeof MANUAL_REVIEW;
    customTitle?: string;
    /** Next step */
    next?: StepId;
    reason?: string;
  }

  export const manualReviewJsonFormat: JsonFormat<ManualReview> =
    objectJsonFormat(
      { type: symbolJsonFormat(MANUAL_REVIEW) },
      {
        customTitle: stringJsonFormat,
        next: bijectionRef(() => stepIdJsonFormat),
        reason: stringJsonFormat,
      },
    );

  /**
   * Flag
   */
  export interface Flag {
    type: typeof FLAG;
    customTitle?: string;
    /** Next step */
    next?: StepId;
    reason?: string;
  }

  export const flagJsonFormat: JsonFormat<Flag> = objectJsonFormat(
    { type: symbolJsonFormat(FLAG) },
    {
      customTitle: stringJsonFormat,
      next: bijectionRef(() => stepIdJsonFormat),
      reason: stringJsonFormat,
    },
  );

  /**
   * Exchange shipping name
   */
  export interface ExchangeShippingName {
    type: typeof EXCHANGE_SHIPPING_NAME;
    customTitle?: string;
    next?: StepId;
    shippingName: string;
  }

  export const exchangeShippingNameJsonFormat: JsonFormat<ExchangeShippingName> =
    objectJsonFormat(
      {
        type: symbolJsonFormat(EXCHANGE_SHIPPING_NAME),
        shippingName: stringJsonFormat,
      },
      {
        customTitle: stringJsonFormat,
        next: bijectionRef(() => stepIdJsonFormat),
      },
    );

  /**
   * Discount code name
   */
  export interface DiscountCodeName {
    type: typeof DISCOUNT_CODE_NAME;
    customTitle?: string;
    prefix?: string;
    suffix?: string;
    next?: StepId;
  }

  export const discountCodeNameJsonFormat: JsonFormat<DiscountCodeName> =
    objectJsonFormat(
      { type: symbolJsonFormat(DISCOUNT_CODE_NAME) },
      {
        customTitle: stringJsonFormat,
        prefix: stringJsonFormat,
        suffix: stringJsonFormat,
        next: bijectionRef(() => stepIdJsonFormat),
      },
    );

  /**
   * Add tags to the exchange draft order
   */
  export interface AddExchangeOrderTags {
    type: typeof ADD_EXCHANGE_ORDER_TAGS;
    customTitle?: string;
    tags: string[];
    next?: StepId;
  }

  export const addExchangeOrderTagsJsonFormat: JsonFormat<AddExchangeOrderTags> =
    objectJsonFormat(
      {
        type: symbolJsonFormat(ADD_EXCHANGE_ORDER_TAGS),
        tags: arrayJsonFormat(stringJsonFormat),
      },
      {
        customTitle: stringJsonFormat,
        next: bijectionRef(() => stepIdJsonFormat),
      },
    );

  /**
   * Override adjustment
   */
  export interface OverrideAdjustment {
    type: typeof OVERRIDE_ADJUSTMENT;
    customTitle?: string;
    /** Next step */
    next?: StepId;
    /** Adjustment */
    adjustment: Adjustment;
    /** Whether the adjustment bonus is use-it-or-lose-it */
    isUseItOrLoseIt?: boolean;
  }

  export const overrideAdjustmentJsonFormat: JsonFormat<OverrideAdjustment> =
    objectJsonFormat(
      {
        type: symbolJsonFormat(OVERRIDE_ADJUSTMENT),
        adjustment: adjustmentJsonFormat,
      },
      {
        customTitle: stringJsonFormat,
        next: bijectionRef(() => stepIdJsonFormat),
        isUseItOrLoseIt: booleanJsonFormat,
      },
    );

  export interface OverrideGreenReturn {
    type: typeof OVERRIDE_GREEN_RETURN;
    customTitle?: string;
    next?: StepId;
    greenReturnOverride: boolean;
  }

  export const overrideGreenReturnJsonFormat: JsonFormat<OverrideGreenReturn> =
    objectJsonFormat(
      {
        type: symbolJsonFormat(OVERRIDE_GREEN_RETURN),
        greenReturnOverride: booleanJsonFormat,
      },
      {
        customTitle: stringJsonFormat,
        next: bijectionRef(() => stepIdJsonFormat),
      },
    );

  /**
   * Customer multiple-choice question
   */
  export interface MultipleChoice {
    type: typeof MULTIPLE_CHOICE;
    customTitle?: string;
    /** Message */
    message: string;
    /** Description */
    description?: string;
    /** Options */
    options: MultipleChoiceOption[];
    /** Set the return reason from the answer to this question. */
    reason: boolean;
    /** Whether to shuffle the options */
    shuffle?: boolean;
    /** Additional information for AI exchange suggestions */
    additionalAiInfo?: string;
  }

  export const multipleChoiceJsonFormat: JsonFormat<MultipleChoice> =
    objectJsonFormat(
      {
        type: symbolJsonFormat(MULTIPLE_CHOICE),
        message: stringJsonFormat,
        options: arrayJsonFormat(multipleChoiceOptionJsonFormat),
        reason: booleanJsonFormat,
      },
      {
        customTitle: stringJsonFormat,
        shuffle: booleanJsonFormat,
        description: stringJsonFormat,
        additionalAiInfo: stringJsonFormat,
      },
    );

  /**
   * Chat multiple choice question
   */
  export interface ChatMultipleChoice {
    type: typeof CHAT_MULTIPLE_CHOICE;
    customTitle?: string;
    /** Message */
    message: string;
    messageHtml?: string;
    /** Options */
    options: ChatMultipleChoiceOption[];
  }

  export const chatMultipleChoiceJsonFormat: JsonFormat<ChatMultipleChoice> =
    objectJsonFormat(
      {
        type: symbolJsonFormat(CHAT_MULTIPLE_CHOICE),
        message: stringJsonFormat,
        options: arrayJsonFormat(chatMultipleChoiceOptionJsonFormat),
      },
      { customTitle: stringJsonFormat, messageHtml: stringJsonFormat },
    );

  /**
   * Rejection
   */
  export interface Reject {
    type: typeof REJECT;
    customTitle?: string;
    /** Message */
    message: string;
  }

  export const rejectJsonFormat: JsonFormat<Reject> = objectJsonFormat(
    { type: symbolJsonFormat(REJECT), message: stringJsonFormat },
    { customTitle: stringJsonFormat },
  );

  /**
   * Information
   */
  export interface Information {
    type: typeof INFORMATION;
    customTitle?: string;
    /** Message */
    message: string;
    header: string;
    continueButtonText: string;
    /** Next step */
    next?: StepId;
  }

  export const informationJsonFormat: JsonFormat<Information> =
    objectJsonFormat(
      {
        type: symbolJsonFormat(INFORMATION),
        message: stringJsonFormat,
        header: stringJsonFormat,
        continueButtonText: stringJsonFormat,
      },
      {
        customTitle: stringJsonFormat,
        next: bijectionRef(() => stepIdJsonFormat),
      },
    );

  export interface ExchangeAgentOverrides {
    // Note: These will either be true or undefined.
    // Meaning it can turn off Exchange Agent for a specific step if it is on for the merchant, but not the other way around.
    disableExchangeAgent?: boolean;
    disableVariantRecommendations?: boolean;
    disableProductRecommendations?: boolean;
  }

  export const exchangeAgentOverridesJsonFormat: JsonFormat<ExchangeAgentOverrides> =
    objectJsonFormat(
      {},
      {
        disableExchangeAgent: booleanJsonFormat,
        disableVariantRecommendations: booleanJsonFormat,
        disableProductRecommendations: booleanJsonFormat,
      },
    );

  // Average label cost adjustment across all merchants, used as the default for new return steps
  export const DEFAULT_LABEL_UPCHARGE_VALUE = "1.17";

  export enum LabelUpchargeType {
    FIXED = "fixed",
    PERCENTAGE = "percentage",
  }

  export const labelUpchargeTypeJsonFormat: JsonFormat<LabelUpchargeType> =
    stringEnumJsonFormat(LabelUpchargeType);

  export interface DropoffFee {
    provider: DropoffProviderType;
    fee: string;
    /**
     * Per-currency flat amounts for this provider's drop-off fee. Keys are
     * Currency enum values; values are the fee expressed in that presentment
     * currency. When a customer's currency matches a key and
     * {@link perCurrencyAmountsEnabled} is true, that amount is used instead
     * of converting `fee` from the shop currency.
     */
    currencyOverrides?: Partial<Record<Currency, number>>;
    perCurrencyAmountsEnabled?: boolean;
  }

  const dropoffFeeJsonFormat: JsonFormat<DropoffFee> = objectJsonFormat(
    {
      provider: stringJsonFormat as JsonFormat<DropoffProviderType>,
      fee: stringJsonFormat,
    },
    {
      currencyOverrides: currencyOverridesJsonFormat,
      perCurrencyAmountsEnabled: booleanJsonFormat,
    },
  );

  /**
   * Return
   */
  export interface Return {
    type: typeof RETURN;
    customTitle?: string;
    /** Options */
    options: ReturnOption[];
    /** Whether to use a flat shipping rate or not */
    flatShipping?: boolean;
    /** The value of the flat shipping rate, if flat shipping is enabled */
    flatShippingRate?: string;
    /** Per-currency flat shipping amounts, keyed by Currency. Used instead of converting flatShippingRate when the customer's currency matches. */
    flatShippingCurrencyOverrides?: Partial<Record<Currency, number>>;
    /** Whether per-currency flat shipping amounts are enabled */
    flatShippingPerCurrencyAmountsEnabled?: boolean;
    /** Flat fees per drop-off provider */
    dropoffFees?: DropoffFee[];

    /** Override Exchange Agent settings */
    exchangeAgentOverrides?: ExchangeAgentOverrides;
    /** Whether the merchant covers package pickup costs for this return block */
    coverPackagePickup?: boolean;
    /** Whether to apply a label cost upcharge/downcharge */
    labelUpcharge?: boolean;
    /** Type of upcharge: "fixed" for fixed amount, "percentage" for percentage */
    labelUpchargeType?: LabelUpchargeType;
    /** The upcharge value (positive = upcharge, negative = downcharge) */
    labelUpchargeValue?: string;
  }

  export const returnJsonFormat: JsonFormat<Return> = objectJsonFormat(
    {
      type: symbolJsonFormat(RETURN),
      options: arrayJsonFormat(returnOptionJsonFormat),
    },
    {
      customTitle: stringJsonFormat,
      flatShipping: booleanJsonFormat,
      flatShippingRate: stringJsonFormat,
      flatShippingCurrencyOverrides: currencyOverridesJsonFormat,
      flatShippingPerCurrencyAmountsEnabled: booleanJsonFormat,
      dropoffFees: arrayJsonFormat(dropoffFeeJsonFormat),
      exchangeAgentOverrides: exchangeAgentOverridesJsonFormat,
      coverPackagePickup: booleanJsonFormat,
      labelUpcharge: booleanJsonFormat,
      labelUpchargeType: labelUpchargeTypeJsonFormat,
      labelUpchargeValue: stringJsonFormat,
    },
  );

  /**
   * Submit Claim
   */
  export interface SubmitClaim {
    type: typeof SUBMIT_CLAIM;
    customTitle?: string;
    option: ClaimOption;
  }

  export const submitClaimJsonFormat: JsonFormat<SubmitClaim> =
    objectJsonFormat(
      { type: symbolJsonFormat(SUBMIT_CLAIM), option: claimOptionJsonFormat },
      { customTitle: stringJsonFormat },
    );

  export interface WarrantyOption {
    /** Return method */
    method: ReturnOptionMethod;
    requireSendBack: boolean;
    adjustment: Adjustment | undefined;
    labelPayer?: UncoveredPayer;
    /**
     * If true, the merchant will provide the customer with a label (and
     * figure out charging them). requireSendBack should be true because
     * the customer is still shipping the item back. Labels will NOT be
     * covered.
     */
    manual_label?: boolean;
    /**
     * Title used for the discount applied to the warranty exchange draft
     * order. Only meaningful when method is a same-item exchange.
     */
    discountTitle?: string;
    /**
     * Whether to charge the customer for the new order's shipping label
     * on the warranty exchange draft order. Only meaningful when method
     * is a same-item exchange — has no effect for refund / store-credit
     * / advanced-exchange warranty outcomes.
     */
    chargeCustomerForNewOrderShipping?: boolean;
    /**
     * Whether to carry over Shopify line item properties (e.g. engravings,
     * monograms) from the returned line item to the new same-item exchange
     * line item. Defaults to true when undefined.
     */
    carryOverLineItemProperties?: boolean;
  }

  export const warrantyOptionJsonFormat: JsonFormat<WarrantyOption> =
    objectJsonFormat(
      {
        method: returnOptionMethodJsonFormat,
        requireSendBack: booleanJsonFormat,
      },
      {
        labelPayer: uncoveredPayerJsonFormat,
        adjustment: adjustmentJsonFormat,
        manual_label: booleanJsonFormat,
        discountTitle: stringJsonFormat,
        chargeCustomerForNewOrderShipping: booleanJsonFormat,
        carryOverLineItemProperties: booleanJsonFormat,
      },
    );

  export interface SubmitWarranty {
    type: typeof SUBMIT_WARRANTY;
    customTitle?: string;
    option: WarrantyOption;
  }

  export const submitWarrantyJsonFormat: JsonFormat<SubmitWarranty> =
    objectJsonFormat(
      {
        type: symbolJsonFormat(SUBMIT_WARRANTY),
        option: warrantyOptionJsonFormat,
      },
      { customTitle: stringJsonFormat },
    );

  export interface SubmitWarrantyRegistration {
    type: typeof SUBMIT_WARRANTY_REGISTRATION;
    customTitle?: string;
  }

  export const submitWarrantyRegistrationJsonFormat: JsonFormat<SubmitWarrantyRegistration> =
    objectJsonFormat(
      { type: symbolJsonFormat(SUBMIT_WARRANTY_REGISTRATION) },
      { customTitle: stringJsonFormat },
    );

  export interface SubmitRepair {
    type: typeof SUBMIT_REPAIR;
    customTitle?: string;
    option?: { labelPayer?: UncoveredPayer; chargeFee?: boolean };
  }

  export const submitRepairJsonFormat: JsonFormat<SubmitRepair> =
    objectJsonFormat(
      { type: symbolJsonFormat(SUBMIT_REPAIR) },
      {
        customTitle: stringJsonFormat,
        option: objectJsonFormat(
          {},
          {
            labelPayer: uncoveredPayerJsonFormat,
            chargeFee: booleanJsonFormat,
          },
        ),
      },
    );

  /**
   * Block
   */
  export interface Block {
    type: typeof BLOCK;
    customTitle?: string;
    message: string;
  }

  export const blockJsonFormat: JsonFormat<Block> = objectJsonFormat(
    { type: symbolJsonFormat(BLOCK), message: stringJsonFormat },
    { customTitle: stringJsonFormat },
  );

  /**
   * Send message
   */
  export interface SendMessage {
    type: typeof Step.SEND_MESSAGE;
    customTitle?: string;
    next: StepId;
    message: string;
    messageHtml?: string;
  }
  export const sendMessageJsonFormat: JsonFormat<SendMessage> =
    objectJsonFormat(
      {
        type: symbolJsonFormat(Step.SEND_MESSAGE),
        next: bijectionRef(() => stepIdJsonFormat),
        message: stringJsonFormat,
      },
      { customTitle: stringJsonFormat, messageHtml: stringJsonFormat },
    );

  /**
   * Close chat
   */
  export interface CloseChat {
    type: typeof CLOSE_CHAT;
    customTitle?: string;
    message: string;
    messageHtml?: string;
  }

  export const closeChatJsonFormat: JsonFormat<CloseChat> = objectJsonFormat(
    { type: symbolJsonFormat(CLOSE_CHAT), message: stringJsonFormat },
    { customTitle: stringJsonFormat, messageHtml: stringJsonFormat },
  );

  /**
   * Reentry
   */
  export interface Reentry {
    type: typeof Step.REENTRY;
    customTitle?: string;
    message: string;
    options: MultipleChoiceOption[];
  }
  export const reentryJsonFormat: JsonFormat<Reentry> = objectJsonFormat(
    {
      type: symbolJsonFormat(Step.REENTRY),
      message: stringJsonFormat,
      options: arrayJsonFormat(multipleChoiceOptionJsonFormat),
    },
    { customTitle: stringJsonFormat },
  );

  /**
   * Assign to agent
   */
  export interface AssignToAgent {
    type: typeof Step.ASSIGN_TO_AGENT;
    customTitle?: string;
    message: string;
    messageHtml?: string;
    emailPromptMessage: string;
    emailPromptMessageHtml?: string;
    followUpMessage: string;
    followUpMessageHtml?: string;
    channel: ConversationPlatform;
    emailToSendAs?: { email: string; name: string; integrationKind: string };
  }

  export const assignToAgentJsonFormat: JsonFormat<AssignToAgent> =
    objectJsonFormat(
      {
        type: symbolJsonFormat(Step.ASSIGN_TO_AGENT),
        message: stringJsonFormat,
        emailPromptMessage: stringJsonFormat,
        followUpMessage: stringJsonFormat,
        channel: conversationPlatformJsonFormat,
      },
      {
        customTitle: stringJsonFormat,
        emailToSendAs: emailToSendAsJsonFormat,
        messageHtml: stringJsonFormat,
        emailPromptMessageHtml: stringJsonFormat,
        followUpMessageHtml: stringJsonFormat,
      },
    );

  /**
   * Customer response
   */
  export interface CustomerResponse {
    type: typeof Step.CUSTOMER_RESPONSE;
    customTitle?: string;
    next: StepId;
    message: string;
    messageHtml?: string;
    infoToCollect: InfoToCollect;
  }

  export const customerResponseJsonFormat: JsonFormat<CustomerResponse> =
    objectJsonFormat(
      {
        type: symbolJsonFormat(Step.CUSTOMER_RESPONSE),
        next: bijectionRef(() => stepIdJsonFormat),
        message: stringJsonFormat,
        infoToCollect: infoToCollectJsonFormat,
      },
      { customTitle: stringJsonFormat, messageHtml: stringJsonFormat },
    );

  /**
   * See orders
   */
  export interface SeeOrders {
    type: typeof Step.SEE_ORDERS;
    customTitle?: string;
    message: string;
    messageHtml?: string;
    next: StepId;
  }

  export const seeOrdersJsonFormat: JsonFormat<SeeOrders> = objectJsonFormat(
    {
      type: symbolJsonFormat(Step.SEE_ORDERS),
      message: stringJsonFormat,
      next: bijectionRef(() => stepIdJsonFormat),
    },
    { customTitle: stringJsonFormat, messageHtml: stringJsonFormat },
  );

  // Silent step: adds tag(s) then advances to `next` with no customer message.
  export interface AddConversationTag {
    type: typeof ADD_CONVERSATION_TAG;
    customTitle?: string;
    tags: { name: string; color: string }[];
    next: StepId;
  }

  export const addConversationTagJsonFormat: JsonFormat<AddConversationTag> =
    objectJsonFormat(
      {
        type: symbolJsonFormat(ADD_CONVERSATION_TAG),
        tags: arrayJsonFormat(
          objectJsonFormat(
            { name: stringJsonFormat, color: stringJsonFormat },
            {},
          ),
        ),
        next: bijectionRef(() => stepIdJsonFormat),
      },
      { customTitle: stringJsonFormat },
    );

  export interface Trigger {
    type: typeof Step.TRIGGER;
    customTitle?: string;
    triggerType: TriggerType;
    next: StepId;
  }

  export const triggerJsonFormat: JsonFormat<Trigger> = objectJsonFormat(
    {
      type: symbolJsonFormat(Step.TRIGGER),
      triggerType: triggerTypeJsonFormat,
      next: bijectionRef(() => stepIdJsonFormat),
    },
    { customTitle: stringJsonFormat },
  );

  export interface Wait {
    type: typeof Step.WAIT;
    customTitle?: string;
    numDays: number;
    next: StepId;
  }

  export const waitJsonFormat: JsonFormat<Wait> = objectJsonFormat(
    {
      type: symbolJsonFormat(Step.WAIT),
      numDays: numberJsonFormat,
      next: bijectionRef(() => stepIdJsonFormat),
    },
    { customTitle: stringJsonFormat },
  );

  export interface SendEmail {
    type: typeof Step.SEND_EMAIL;
    emailId: string;
    emailName?: string;
    emailType?: string;
    customTitle?: string;
    next?: StepId;
  }

  export const sendEmailJsonFormat: JsonFormat<SendEmail> = objectJsonFormat(
    { type: symbolJsonFormat(Step.SEND_EMAIL), emailId: stringJsonFormat },
    {
      customTitle: stringJsonFormat,
      emailName: stringJsonFormat,
      emailType: stringJsonFormat,
      next: bijectionRef(() => stepIdJsonFormat),
    },
  );

  export interface SendSms {
    type: typeof Step.SEND_SMS;
    smsId: string;
    smsName?: string;
    smsType?: string;
    customTitle?: string;
    next?: StepId;
  }

  export const sendSmsJsonFormat: JsonFormat<SendSms> = objectJsonFormat(
    { type: symbolJsonFormat(Step.SEND_SMS), smsId: stringJsonFormat },
    {
      customTitle: stringJsonFormat,
      smsName: stringJsonFormat,
      smsType: stringJsonFormat,
      next: bijectionRef(() => stepIdJsonFormat),
    },
  );

  export const multipleActionsJsonFormat: JsonFormat<MultipleActions> =
    objectJsonFormat(
      {
        type: symbolJsonFormat(Step.MULTIPLE_ACTIONS),
        actions: arrayJsonFormat(actionJsonFormat),
      },
      { next: bijectionRef(() => stepIdJsonFormat) },
    );

  export const multipleConditionsJsonFormat: JsonFormat<MultipleConditions> =
    objectJsonFormat(
      {
        type: symbolJsonFormat(Step.MULTIPLE_CONDITIONS),
        conditions: arrayJsonFormat(conditionJsonFormat),
      },
      {
        nextTrue: bijectionRef(() => stepIdJsonFormat),
        nextFalse: bijectionRef(() => stepIdJsonFormat),
      },
    );

  /**
   * Multiple Actions in a single step
   */
  export interface MultipleActions {
    type: typeof MULTIPLE_ACTIONS;
    actions: Action[];
    next?: StepId;
  }

  /**
   * Multiple Actions in a single step
   */
  export interface MultipleConditions {
    type: typeof MULTIPLE_CONDITIONS;
    conditions: Condition[];
    nextTrue?: StepId;
    /** Next step if false */
    nextFalse?: StepId;
  }

  /**
   * Final sale return
   */
  export interface FinalSaleReturn {
    type: typeof FINAL_SALE_RETURN;
    /** Next step */
    next?: StepId;
  }

  /**
   * Do nothing
   */
  export interface DoNothing {
    type: typeof DO_NOTHING;
  }

  export const finalSaleJsonFormat: JsonFormat<FinalSaleReturn> =
    objectJsonFormat(
      { type: symbolJsonFormat(FINAL_SALE_RETURN) },
      { next: bijectionRef(() => stepIdJsonFormat) },
    );

  /**
   * AB test
   */
  export interface ABTest {
    type: typeof AB_TEST;
    name: string;
    customTitle?: string;
    /** Options */
    treatments: ABTestTreatment[];
    /** ISO date string of when this AB test was first activated (published). */
    startedAt?: string;
  }

  export const ABTestJsonFormat: JsonFormat<ABTest> = objectJsonFormat(
    {
      type: symbolJsonFormat(AB_TEST),
      treatments: arrayJsonFormat(abTestTreatmentJsonFormat),
      name: stringJsonFormat,
    },
    { customTitle: stringJsonFormat, startedAt: stringJsonFormat },
  );

  /**
   * EW Upsell
   */
  export interface EWUpsell {
    type: typeof EW_UPSELL;
    next: StepId;
    fieldId?: FieldId;
  }

  export const ewUpsellJsonFormat: JsonFormat<EWUpsell> = objectJsonFormat(
    {
      type: symbolJsonFormat(EW_UPSELL),
      next: bijectionRef(() => stepIdJsonFormat),
    },
    { fieldId: stringEnumJsonFormat(FieldId) },
  );

  /**
   * AI Return Reasons
   */
  /**
   * Used for backwards compatibility for exchange intent becoming a custom guidance
   */
  export const EXCHANGE_GUIDANCE_ID = "exchange";

  export interface CustomGuidance {
    /** Stable id (uuid) generated when the merchant creates the rule. */
    id: string;
    /** Human-readable description shown to merchants and to the AI agent. */
    description: string;
    /** Step to route to when the AI matches this rule. */
    next: StepId;
  }

  export const customGuidanceJsonFormat: JsonFormat<CustomGuidance> =
    objectJsonFormat(
      {
        id: stringJsonFormat,
        description: stringJsonFormat,
        next: bijectionRef(() => stepIdJsonFormat),
      },
      {},
    );

  export interface AiReturnReasons {
    type: typeof AI_RETURN_REASONS;
    next: StepId;
    nextWrongProduct: StepId;
    nextDamaged: StepId;
    /**
     * @deprecated Exchange routing now lives in `customGuidance` with the
     * canonical id `"exchange"`. Old documents may still have this field; on
     * read it is auto-promoted into `customGuidance`. New writes never set it.
     */
    nextExchange?: StepId;
    /**
     * Merchant-defined custom guidance rules consulted only when none of the
     * built-in damaged/wrong-product branches apply.
     */
    customGuidance?: CustomGuidance[];
    additionalAiInfo?: string;
    maxQuestions?: number;
    merchantGuidance?: string;
    /** Custom question text to show instead of the default first question. */
    customQuestion?: string;
    /** Custom explanation subtext shown below the main question. */
    customExplanationSubtext?: string;
  }

  export const aiReturnReasonsJsonFormat: JsonFormat<AiReturnReasons> =
    objectJsonFormat(
      {
        type: symbolJsonFormat(AI_RETURN_REASONS),
        next: bijectionRef(() => stepIdJsonFormat),
        nextWrongProduct: bijectionRef(() => stepIdJsonFormat),
        nextDamaged: bijectionRef(() => stepIdJsonFormat),
      },
      {
        nextExchange: bijectionRef(() => stepIdJsonFormat),
        customGuidance: arrayJsonFormat(customGuidanceJsonFormat),
        additionalAiInfo: stringJsonFormat,
        maxQuestions: numberJsonFormat,
        merchantGuidance: stringJsonFormat,
        customQuestion: stringJsonFormat,
        customExplanationSubtext: stringJsonFormat,
      },
    );

  /**
   * Add Return Tag
   */
  export interface AddReturnTag {
    type: typeof ADD_RETURN_TAG;
    customTitle?: string;
    tags: { name: string; color: string }[];
    next?: StepId;
  }

  // Create JSON format for AddReturnTag with backward compatibility
  const addReturnTagBijection: Bijection<any, AddReturnTag> = {
    read(from: any): AddReturnTag {
      // Handle backward compatibility: convert old single-tag format to new multi-tag format
      const tags = from.tags
        ? from.tags // New format: already has tags array
        : from.tagName && from.tagColor
          ? [{ name: from.tagName, color: from.tagColor }] // Old format: convert to array
          : []; // No tags

      return {
        type: symbolJsonFormat(ADD_RETURN_TAG).read(from.type),
        customTitle: from.customTitle,
        tags,
        next: from.next ? stepIdJsonFormat.read(from.next) : undefined,
      };
    },
    write(to: AddReturnTag): any {
      return {
        type: symbolJsonFormat(ADD_RETURN_TAG).write(to.type),
        customTitle: to.customTitle,
        tags: to.tags,
        next: to.next ? stepIdJsonFormat.write(to.next) : undefined,
      };
    },
  };

  export const addReturnTagJsonFormat: JsonFormat<AddReturnTag> =
    addReturnTagBijection;

  /**
   * Dynamic Bundle Adjustment
   */
  export interface DynamicBundleAdjustment {
    type: typeof DYNAMIC_BUNDLE_ADJUSTMENT;
    customTitle?: string;
    next?: StepId;
  }

  export const dynamicBundleAdjustmentJsonFormat: JsonFormat<DynamicBundleAdjustment> =
    objectJsonFormat(
      { type: symbolJsonFormat(DYNAMIC_BUNDLE_ADJUSTMENT) },
      { customTitle: stringJsonFormat, next: numberJsonFormat },
    );

  export interface LocationWithAddress {
    name: string;
    address: {
      street1: string;
      street2?: string;
      city: string;
      state?: string;
      zip: string;
      country: string;
      phone?: string;
    };
    aims360WarehouseId?: string;
    trackstarWarehouseId?: string;
    inmarLocationId?: string;
    blueYonderLocationId?: string;
    isSekoHub?: boolean;
    externalReturnHub?: {
      provider: ExternalReturnHubType;
      locationId?: string | null;
    };
    returnsCenterId?: string;
    locationId?: string;
    externalLocationId?: string;
  }

  export const locationWithAddressJsonFormat: JsonFormat<LocationWithAddress> =
    objectJsonFormat(
      {
        name: stringJsonFormat,
        address: objectJsonFormat(
          {
            street1: stringJsonFormat,
            city: stringJsonFormat,
            zip: stringJsonFormat,
            country: stringJsonFormat,
          },
          {
            street2: stringJsonFormat,
            state: stringJsonFormat,
            phone: stringJsonFormat,
          },
        ),
      },
      {
        aims360WarehouseId: stringJsonFormat,
        trackstarWarehouseId: stringJsonFormat,
        inmarLocationId: stringJsonFormat,
        blueYonderLocationId: stringJsonFormat,
        isSekoHub: booleanJsonFormat,
        externalReturnHub: objectJsonFormat(
          { provider: stringEnumJsonFormat(ExternalReturnHubType) },
          { locationId: nullableJsonFormat(stringJsonFormat) },
        ),
        returnsCenterId: stringJsonFormat,
        locationId: stringJsonFormat,
        externalLocationId: stringJsonFormat,
      },
    );

  export interface SetReturnLocations {
    type: typeof SET_RETURN_LOCATIONS;
    customTitle?: string;
    locations: string[];
    restockExternalLocationIds?: Record<string, string | null>;
    useOriginalLocation?: boolean;
    useOriginalFulfillmentAddress?: boolean;
    useRedoManaged?: boolean;
    useDonation?: boolean;
    rateSourceSelection?: ReturnRateSourceSelection;
  }

  const restockExternalLocationIdsFormat: JsonFormat<
    Record<string, string | null>
  > = {
    read(json: Json) {
      if (typeof json !== "object" || json instanceof Array || !json) {
        return {};
      }
      const result: Record<string, string | null> = {};
      for (const key in json) {
        const value = (json as Record<string, Json>)[key];
        result[key] =
          value === null || typeof value === "string" ? value : null;
      }
      return result;
    },
    write(record: Record<string, string | null>) {
      const json: Record<string, string | null> = {};
      for (const key in record) {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        json[key] = record[key];
      }
      return json;
    },
  };

  const rateSourceSelectionJsonFormat: JsonFormat<ReturnRateSourceSelection> = {
    read(json: Json) {
      if (typeof json !== "object" || json instanceof Array || !json) {
        return { sources: [] };
      }
      const rawSources = (json as Record<string, Json>).sources;
      if (!(rawSources instanceof Array)) {
        return { sources: [] };
      }

      const sources: ReturnRateSource[] = [];
      for (const rawSource of rawSources) {
        if (
          typeof rawSource !== "object" ||
          rawSource instanceof Array ||
          !rawSource
        ) {
          continue;
        }

        const source = rawSource as Record<string, Json>;
        switch (source.type) {
          case RETURN_RATE_SOURCE_TYPE.REDO_RATES:
            sources.push({ type: RETURN_RATE_SOURCE_TYPE.REDO_RATES });
            break;
          case RETURN_RATE_SOURCE_TYPE.MERCHANT_CARRIER_ACCOUNTS:
            sources.push({
              type: RETURN_RATE_SOURCE_TYPE.MERCHANT_CARRIER_ACCOUNTS,
            });
            break;
          case RETURN_RATE_SOURCE_TYPE.MERCHANT_CARRIER_ACCOUNT:
            if (typeof source.carrierAccountId === "string") {
              const deniedServiceLevels =
                source.deniedServiceLevels instanceof Array
                  ? source.deniedServiceLevels.filter(
                      (serviceLevel): serviceLevel is string =>
                        typeof serviceLevel === "string",
                    )
                  : [];
              sources.push({
                type: RETURN_RATE_SOURCE_TYPE.MERCHANT_CARRIER_ACCOUNT,
                carrierAccountId: source.carrierAccountId,
                ...(deniedServiceLevels.length ? { deniedServiceLevels } : {}),
              });
            }
            break;
          case RETURN_RATE_SOURCE_TYPE.DROPOFF_PROVIDER:
            if (isDropoffProviderType(source.provider)) {
              sources.push({
                type: RETURN_RATE_SOURCE_TYPE.DROPOFF_PROVIDER,
                provider: source.provider,
              });
            }
            break;
        }
      }

      return { sources };
    },
    write(selection: ReturnRateSourceSelection) {
      return {
        sources: selection.sources.map((source) => {
          switch (source.type) {
            case RETURN_RATE_SOURCE_TYPE.REDO_RATES:
              return { type: RETURN_RATE_SOURCE_TYPE.REDO_RATES };
            case RETURN_RATE_SOURCE_TYPE.MERCHANT_CARRIER_ACCOUNTS:
              return {
                type: RETURN_RATE_SOURCE_TYPE.MERCHANT_CARRIER_ACCOUNTS,
              };
            case RETURN_RATE_SOURCE_TYPE.MERCHANT_CARRIER_ACCOUNT:
              return {
                type: RETURN_RATE_SOURCE_TYPE.MERCHANT_CARRIER_ACCOUNT,
                carrierAccountId: source.carrierAccountId,
                ...(source.deniedServiceLevels?.length
                  ? { deniedServiceLevels: [...source.deniedServiceLevels] }
                  : {}),
              };
            case RETURN_RATE_SOURCE_TYPE.DROPOFF_PROVIDER:
              return {
                type: RETURN_RATE_SOURCE_TYPE.DROPOFF_PROVIDER,
                provider: source.provider,
              };
          }
        }),
      };
    },
  };

  export const setReturnLocationsJsonFormat: JsonFormat<SetReturnLocations> =
    objectJsonFormat(
      {
        type: symbolJsonFormat(SET_RETURN_LOCATIONS),
        locations: arrayJsonFormat(stringJsonFormat),
      },
      {
        customTitle: stringJsonFormat,
        restockExternalLocationIds: restockExternalLocationIdsFormat,
        useOriginalLocation: booleanJsonFormat,
        useOriginalFulfillmentAddress: booleanJsonFormat,
        useRedoManaged: booleanJsonFormat,
        useDonation: booleanJsonFormat,
        rateSourceSelection: rateSourceSelectionJsonFormat,
      },
    );

  export interface CombineLabel {
    type: typeof COMBINE_LABEL;
    customTitle?: string;
  }

  export const combineLabelJsonFormat: JsonFormat<CombineLabel> =
    objectJsonFormat(
      { type: symbolJsonFormat(COMBINE_LABEL) },
      { customTitle: stringJsonFormat },
    );

  export interface SeparateLabels {
    type: typeof SEPARATE_LABELS;
    customTitle?: string;
  }

  export const separateLabelsJsonFormat: JsonFormat<SeparateLabels> =
    objectJsonFormat(
      { type: symbolJsonFormat(SEPARATE_LABELS) },
      { customTitle: stringJsonFormat },
    );

  export type ReturnProcessingStepId = number;

  export const returnProcessingStepScope = ["return", "return_item"] as const;
  export type ReturnProcessingStepScope =
    (typeof returnProcessingStepScope)[number];

  export interface ReturnProcessingDecision {
    type: typeof RETURN_PROCESSING_DECISION;
    prompt: string;
    trueLabel: string;
    falseLabel: string;
    nextTrueId: ReturnProcessingStepId | null;
    nextFalseId: ReturnProcessingStepId | null;
    scope: ReturnProcessingStepScope;
    customTitle?: string;
    decisionKey?: string;
    imageUrl?: string;
  }

  export const returnProcessingDecisionJsonFormat: JsonFormat<ReturnProcessingDecision> =
    objectJsonFormat(
      {
        type: symbolJsonFormat(RETURN_PROCESSING_DECISION),
        prompt: stringJsonFormat,
        trueLabel: stringJsonFormat,
        falseLabel: stringJsonFormat,
        nextTrueId: nullableJsonFormat(numberJsonFormat),
        nextFalseId: nullableJsonFormat(numberJsonFormat),
        scope: stringUnionJsonFormat(returnProcessingStepScope),
      },
      {
        customTitle: stringJsonFormat,
        decisionKey: stringJsonFormat,
        imageUrl: stringJsonFormat,
      },
    );

  export interface LicensePlate {
    type: typeof LICENSE_PLATE;
    next?: ReturnProcessingStepId | null;
    scope: ReturnProcessingStepScope;
    customTitle?: string;
  }

  export const licensePlateJsonFormat: JsonFormat<LicensePlate> =
    objectJsonFormat(
      {
        type: symbolJsonFormat(LICENSE_PLATE),
        scope: stringUnionJsonFormat(returnProcessingStepScope),
      },
      {
        customTitle: stringJsonFormat,
        next: nullableJsonFormat(numberJsonFormat),
      },
    );

  export interface Sort {
    type: typeof SORT;
    next?: ReturnProcessingStepId | null;
    scope: ReturnProcessingStepScope;
    customTitle?: string;
    destination: string;
  }

  export const sortJsonFormat: JsonFormat<Sort> = objectJsonFormat(
    {
      type: symbolJsonFormat(SORT),
      scope: stringUnionJsonFormat(returnProcessingStepScope),
      destination: stringJsonFormat,
    },
    {
      customTitle: stringJsonFormat,
      next: nullableJsonFormat(numberJsonFormat),
    },
  );

  export interface ReturnProcessingInformation {
    type: typeof RETURN_PROCESSING_INFORMATION;
    header: string;
    message: string;
    continueButtonText: string;
    next?: ReturnProcessingStepId | null;
    scope: ReturnProcessingStepScope;
    customTitle?: string;
    imageUrl?: string;
  }

  export const returnProcessingInformationJsonFormat: JsonFormat<ReturnProcessingInformation> =
    objectJsonFormat(
      {
        type: symbolJsonFormat(RETURN_PROCESSING_INFORMATION),
        header: stringJsonFormat,
        message: stringJsonFormat,
        continueButtonText: stringJsonFormat,
        scope: stringUnionJsonFormat(returnProcessingStepScope),
      },
      {
        customTitle: stringJsonFormat,
        next: nullableJsonFormat(numberJsonFormat),
        imageUrl: stringJsonFormat,
      },
    );

  export interface Evidence {
    type: typeof EVIDENCE;
    prompt: string;
    notesLabel?: string;
    notesPlaceholder?: string;
    next?: ReturnProcessingStepId | null;
    scope: ReturnProcessingStepScope;
    customTitle?: string;
    imageUrl?: string;
  }

  export const evidenceJsonFormat: JsonFormat<Evidence> = objectJsonFormat(
    {
      type: symbolJsonFormat(EVIDENCE),
      prompt: stringJsonFormat,
      scope: stringUnionJsonFormat(returnProcessingStepScope),
    },
    {
      notesLabel: stringJsonFormat,
      notesPlaceholder: stringJsonFormat,
      next: nullableJsonFormat(numberJsonFormat),
      customTitle: stringJsonFormat,
      imageUrl: stringJsonFormat,
    },
  );

  export interface AddUndeclaredItem {
    type: typeof ADD_UNDECLARED_ITEM;
    prompt: string;
    next?: ReturnProcessingStepId | null;
    scope: ReturnProcessingStepScope;
    customTitle?: string;
  }

  export const addUndeclaredItemJsonFormat: JsonFormat<AddUndeclaredItem> =
    objectJsonFormat(
      {
        type: symbolJsonFormat(ADD_UNDECLARED_ITEM),
        prompt: stringJsonFormat,
        scope: stringUnionJsonFormat(returnProcessingStepScope),
      },
      {
        next: nullableJsonFormat(numberJsonFormat),
        customTitle: stringJsonFormat,
      },
    );

  export interface Verification {
    type: typeof VERIFICATION;
    prompt: string;
    next?: ReturnProcessingStepId | null;
    scope: ReturnProcessingStepScope;
    customTitle?: string;
  }

  export const verificationJsonFormat: JsonFormat<Verification> =
    objectJsonFormat(
      {
        type: symbolJsonFormat(VERIFICATION),
        prompt: stringJsonFormat,
        scope: stringUnionJsonFormat(returnProcessingStepScope),
      },
      {
        next: nullableJsonFormat(numberJsonFormat),
        customTitle: stringJsonFormat,
      },
    );

  export interface ProcessingAction {
    type: typeof PROCESSING_ACTION;
    action: ProcessingAction_;
    next?: ReturnProcessingStepId | null;
    scope: ReturnProcessingStepScope;
    customTitle?: string;
  }

  export const processingActionJsonFormat: JsonFormat<ProcessingAction> =
    objectJsonFormat(
      {
        type: symbolJsonFormat(PROCESSING_ACTION),
        action: processingActionJsonFormat_,
        scope: stringUnionJsonFormat(returnProcessingStepScope),
      },
      {
        next: nullableJsonFormat(numberJsonFormat),
        customTitle: stringJsonFormat,
      },
    );

  /**
   * Verify-item-variant step. Worker confirms the physical item matches the
   * variant declared on the return line. Match → proceeds to `next`. Mismatch
   * exits the flow back to the base processing page and is not encoded as a
   * separate branch on the model.
   */
  export interface VerifyItemVariant {
    type: typeof VERIFY_ITEM_VARIANT;
    prompt: string;
    matchLabel: string;
    mismatchLabel: string;
    next?: ReturnProcessingStepId | null;
    scope: ReturnProcessingStepScope;
    customTitle?: string;
  }

  export const verifyItemVariantJsonFormat: JsonFormat<VerifyItemVariant> =
    objectJsonFormat(
      {
        type: symbolJsonFormat(VERIFY_ITEM_VARIANT),
        prompt: stringJsonFormat,
        matchLabel: stringJsonFormat,
        mismatchLabel: stringJsonFormat,
        scope: stringUnionJsonFormat(returnProcessingStepScope),
      },
      {
        next: nullableJsonFormat(numberJsonFormat),
        customTitle: stringJsonFormat,
      },
    );

  export interface RedoManagedLocation {
    type: typeof REDO_MANAGED_LOCATION;
    customTitle?: string;
    returnsCenterIds: string[];
    serviceLevel: ReturnProcessingLevel;
    refulfillmentEnabled?: boolean;
    processingFee?: string;
    consolidationScheduleDays?: string;
    shipToAddress?: ConsolidationShipToAddress;
    externalReturnHub?: ExternalLocationHubConfig;
    rateSourceSelection?: ReturnRateSourceSelection;
  }

  export const redoManagedLocationJsonFormat: JsonFormat<RedoManagedLocation> =
    objectJsonFormat(
      {
        type: symbolJsonFormat(REDO_MANAGED_LOCATION),
        returnsCenterIds: arrayJsonFormat(stringJsonFormat),
        serviceLevel: stringEnumJsonFormat(ReturnProcessingLevel),
      },
      {
        customTitle: stringJsonFormat,
        refulfillmentEnabled: booleanJsonFormat,
        processingFee: stringJsonFormat,
        consolidationScheduleDays: stringJsonFormat,
        shipToAddress: objectJsonFormat(
          {
            name: stringJsonFormat,
            street1: stringJsonFormat,
            city: stringJsonFormat,
            zip: stringJsonFormat,
            country: stringJsonFormat,
          },
          { street2: stringJsonFormat, state: stringJsonFormat },
        ),
        externalReturnHub: objectJsonFormat(
          { provider: stringEnumJsonFormat(ExternalReturnHubType) },
          { locationId: stringJsonFormat },
        ),
        rateSourceSelection: rateSourceSelectionJsonFormat,
      },
    );
}

const stepUnionJsonFormat: JsonFormat<Step | ChatFlowStep> =
  symbolUnionJsonFormat("type", "type", {
    [Step.CONDITION]: Step.conditionJsonFormat,
    [Step.INPUT]: Step.inputJsonFormat,
    [Step.MANUAL_REVIEW]: Step.manualReviewJsonFormat,
    [Step.FLAG]: Step.flagJsonFormat,
    [Step.INFORMATION]: Step.informationJsonFormat,
    [Step.EXCHANGE_SHIPPING_NAME]: Step.exchangeShippingNameJsonFormat,
    [Step.DISCOUNT_CODE_NAME]: Step.discountCodeNameJsonFormat,
    [Step.ADD_EXCHANGE_ORDER_TAGS]: Step.addExchangeOrderTagsJsonFormat,
    [Step.OVERRIDE_ADJUSTMENT]: Step.overrideAdjustmentJsonFormat,
    [Step.OVERRIDE_GREEN_RETURN]: Step.overrideGreenReturnJsonFormat,
    [Step.MULTIPLE_CHOICE]: Step.multipleChoiceJsonFormat,
    [Step.REJECT]: Step.rejectJsonFormat,
    [Step.RETURN]: Step.returnJsonFormat,
    [Step.SUBMIT_CLAIM]: Step.submitClaimJsonFormat,
    [Step.SUBMIT_REPAIR]: Step.submitRepairJsonFormat,
    [Step.SUBMIT_WARRANTY]: Step.submitWarrantyJsonFormat,
    [Step.SUBMIT_WARRANTY_REGISTRATION]:
      Step.submitWarrantyRegistrationJsonFormat,
    [Step.BLOCK]: Step.blockJsonFormat,
    [Step.CHAT_MULTIPLE_CHOICE]: Step.chatMultipleChoiceJsonFormat,
    [Step.CLOSE_CHAT]: Step.closeChatJsonFormat,
    [Step.CUSTOMER_RESPONSE]: Step.customerResponseJsonFormat,
    [Step.SEND_MESSAGE]: Step.sendMessageJsonFormat,
    [Step.ASSIGN_TO_AGENT]: Step.assignToAgentJsonFormat,
    [Step.REENTRY]: Step.reentryJsonFormat,
    [Step.TRIGGER]: Step.triggerJsonFormat,
    [Step.WAIT]: Step.waitJsonFormat,
    [Step.SEND_EMAIL]: Step.sendEmailJsonFormat,
    [Step.SEND_SMS]: Step.sendSmsJsonFormat,
    [Step.SEE_ORDERS]: Step.seeOrdersJsonFormat,
    [Step.ADD_CONVERSATION_TAG]: Step.addConversationTagJsonFormat,
    [Step.ACTION]: Step.actionJsonFormat,
    [Step.MULTIPLE_ACTIONS]: Step.multipleActionsJsonFormat,
    [Step.MULTIPLE_CONDITIONS]: Step.multipleConditionsJsonFormat,
    [Step.DO_NOTHING]: Step.doNothingJsonFormat,
    [Step.AB_TEST]: Step.ABTestJsonFormat,
    [Step.EW_UPSELL]: Step.ewUpsellJsonFormat,
    [Step.AI_RETURN_REASONS]: Step.aiReturnReasonsJsonFormat,
    [Step.ADD_RETURN_TAG]: Step.addReturnTagJsonFormat,
    [Step.DYNAMIC_BUNDLE_ADJUSTMENT]: Step.dynamicBundleAdjustmentJsonFormat,
    [Step.RETURN_PROCESSING_DECISION]: Step.returnProcessingDecisionJsonFormat,
    [Step.SET_RETURN_LOCATIONS]: Step.setReturnLocationsJsonFormat,
    [Step.COMBINE_LABEL]: Step.combineLabelJsonFormat,
    [Step.SEPARATE_LABELS]: Step.separateLabelsJsonFormat,
    [Step.LICENSE_PLATE]: Step.licensePlateJsonFormat,
    [Step.SORT]: Step.sortJsonFormat,
    [Step.RETURN_PROCESSING_INFORMATION]:
      Step.returnProcessingInformationJsonFormat,
    [Step.EVIDENCE]: Step.evidenceJsonFormat,
    [Step.ADD_UNDECLARED_ITEM]: Step.addUndeclaredItemJsonFormat,
    [Step.VERIFICATION]: Step.verificationJsonFormat,
    [Step.PROCESSING_ACTION]: Step.processingActionJsonFormat,
    [Step.VERIFY_ITEM_VARIANT]: Step.verifyItemVariantJsonFormat,
    [Step.REDO_MANAGED_LOCATION]: Step.redoManagedLocationJsonFormat,
  });

/**
 * Wraps the step union codec to preserve the redo-managed marker, which the
 * per-variant object formats would otherwise drop as an undeclared field.
 */
export const stepJsonFormat: JsonFormat<Step | ChatFlowStep> = {
  read(json: Json) {
    const step = stepUnionJsonFormat.read(json) as Step;
    if (
      typeof json === "object" &&
      json !== null &&
      !Array.isArray(json) &&
      (json as JsonObject).redoManaged === true
    ) {
      step.redoManaged = true;
      const fields = (json as JsonObject).redoManagedEditableFields;
      if (Array.isArray(fields)) {
        step.redoManagedEditableFields = fields as RedoManagedEditableField[];
      }
    }
    return step;
  },
  write(value: Step | ChatFlowStep) {
    const json = stepUnionJsonFormat.write(value) as JsonObject;
    const marker = value as RedoManagedMarker;
    if (marker.redoManaged) {
      json.redoManaged = true;
      if (marker.redoManagedEditableFields?.length) {
        json.redoManagedEditableFields = marker.redoManagedEditableFields;
      }
    }
    return json;
  },
};

export interface StringTransforms {
  /** A regex pattern for what to replace */
  search: string;
  /** What to replace the searched pattern with */
  replace: string;
}

export interface BundleRules {
  /** The key that will match the line item property name */
  key: string;
  /** Whether to use the product name as the key */
  keyTransforms?: StringTransforms[];
  /** The type of the line item property value */
  valueType: BundleValueType;
  /** Whether the bundle info is stored on a property in the order */
  isProperty: boolean;
  /** Whether is a merge or expand bundle */
  type: BundleType;
  /* The delimiter used to separate the values (if the property
      value contains multiple values) */
  valueDelimiter: string;
  /** A Regex pattern to parse the property value */
  valueParser: string;
  /** A regex pattern to parse the quantity */
  quantityParser: string;
  /** How we should transform the value after we extract it */
  valueTransforms: StringTransforms[];
}

export interface ExpandBundleRules extends BundleRules {
  bundleType: typeof BundleType.EXPAND;
  variantExchangeOnly: boolean;
}

export interface MergeBundleRules extends BundleRules {
  bundleType: typeof BundleType.MERGE;
}

export interface ParentChildBundleRules extends BundleRules {
  bundleType: typeof BundleType.PARENT_CHILD;
  parentChildBundleType: ParentChildBundleType;
  parentPropertyIdentifier: ParentPropertyIdentifier;
  compareParentPropertyToProductTitle: boolean;
  parentPropertyKey: string;
  parentPropertyTransforms: StringTransforms[];
  useCompareAtPrice: boolean;
}

/**
 * Different than bundle rules. Bundle rules are used in the backend to automatically detect bundles based on properties
 * on the order/line items. Bundles (defined by this interface) are set up manually in the UI by the merchant
 */
export interface Bundle {
  /* The name the merchant gives the bundle */
  name: string;
  parentVariantId: string;
  parts: { variantId: string; returnable: boolean }[];
  allowedTypes: string[];
}

export interface BundleSkus {
  parentSku: string;
  childSkus: string[];
  returnTypes: ReturnTypeEnum[];
}

export interface ShopifyProperty {
  name: string;
  value: string;
}

/** The default index to use for the freSourceItemIndex */
export const LINE_ITEM_SOURCE_INDEX = 0;

export const stepIdJsonFormat: JsonFormat<StepId> = numberJsonFormat;

export function getTreatment(
  treatments: ABTestTreatment[],
  treatmentHash: string,
): ABTestTreatment | undefined {
  const hashPercentage = Number(BigInt("0x" + treatmentHash) % 100n);

  const cumulativePercentages = treatments.map((bucket, index) => {
    return treatments
      .slice(0, index + 1)
      .reduce((sum, b) => sum + b.percentage, 0);
  });

  for (const [i, cumulativePercentage] of cumulativePercentages.entries()) {
    if (hashPercentage < cumulativePercentage) {
      return treatments[i];
    }
  }
  return undefined;
}

export type ReturnProcessingStep =
  | Step.ReturnProcessingDecision
  | Step.ReturnProcessingInformation
  | Step.LicensePlate
  | Step.Sort
  | Step.Evidence
  | Step.AddUndeclaredItem
  | Step.Verification
  | Step.ProcessingAction
  | Step.VerifyItemVariant;

export interface ReturnProcessingFlow {
  type?: FlowType.RETURN_PROCESSING;
  steps: ReturnProcessingStep[];
}
